import React, { useMemo, useState } from 'react';
import { Row, Col, Spinner, Button } from 'react-bootstrap';
import { FaShoppingCart, FaHeadset, FaRobot, FaPercentage, FaChartLine, FaChartBar, FaFileExcel } from 'react-icons/fa';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Pie, Bar, Line } from 'react-chartjs-2';
import CardKPI from '~/Components/RelatorioLeadsVendas/CardKPI';
import { consultarConversoesVendasAnalitico } from '~/Services/Service';
import { salvarEmExcel } from '~/Utils/Helpers';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  PointElement,
  LineElement,
  Tooltip,
  Legend
);

const CORES_GRAFICOS = [
  'rgba(54, 162, 235, 0.8)',
  'rgba(255, 99, 132, 0.8)',
  'rgba(255, 206, 86, 0.8)',
  'rgba(75, 192, 192, 0.8)',
  'rgba(153, 102, 255, 0.8)',
  'rgba(255, 159, 64, 0.8)',
  'rgba(199, 199, 199, 0.8)',
  'rgba(83, 102, 255, 0.8)',
  'rgba(255, 99, 255, 0.8)',
  'rgba(99, 255, 132, 0.8)',
];

const CORES_LINHAS = [
  'rgb(54, 162, 235)',
  'rgb(255, 99, 132)',
  'rgb(255, 206, 86)',
  'rgb(75, 192, 192)',
  'rgb(153, 102, 255)',
  'rgb(255, 159, 64)',
  'rgb(199, 199, 199)',
  'rgb(83, 102, 255)',
  'rgb(255, 99, 255)',
  'rgb(99, 255, 132)',
];

const TabConversoesVendas = ({ dados, loading, montarFiltros }) => {
  const [isLoadingExcel, setIsLoadingExcel] = useState(false);

  const handleExportarExcel = async () => {
    try {
      setIsLoadingExcel(true);
      const filtros = montarFiltros();
      const dadosAnaliticos = await consultarConversoesVendasAnalitico(filtros);

      const dadosExcel = dadosAnaliticos.map(item => ({
        codigo_boleto: item.codigo_boleto,
        codigo_pre_venda: item.codigo_pre_venda,
        codigo_prospecto: item.codigo_prospecto,
        data_venda: item.data_venda,
        valor_venda: item.valor_venda,
        codigo_familia: item.codigo_familia,
        nome_empreendimento: item.nome_empreendimento,
        nome_atendente: item.nome_atendente,
        origem_cav: item.is_origem_cav ? 'Sim' : 'Não',
        origem_stella: item.is_origem_stella ? 'Sim' : 'Não',
      }));

      await salvarEmExcel(dadosExcel, '/exportador/excel/relatorio-leads-vendas/vendas');
    } catch (error) {
      console.error('Erro ao exportar Excel:', error);
    } finally {
      setIsLoadingExcel(false);
    }
  };

  const kpis = useMemo(() => {
    if (!dados) return null;
    return {
      total_vendas: dados.total_vendas || 0,
      vendas_pela_cav: dados.vendas_pela_cav || 0,
      vendas_pela_stella: dados.vendas_pela_stella || 0,
      media_vendas_x_leads: dados.media_vendas_x_leads || 0,
      roi_medio: dados.roi_medio,
      roi_disponivel: dados.roi_disponivel || false,
    };
  }, [dados]);

  const formatarMedia = (valor) => {
    if (!valor) return '0%';
    return `${(valor * 100).toFixed(2)}%`;
  };

  const criarDadosPizza = (lista, label) => {
    if (!lista || !lista.length) return null;
    const total = lista.reduce((acc, item) => acc + (item.valor || 0), 0);
    return {
      labels: lista.map(item => item.label || 'N/I'),
      datasets: [{
        label,
        data: lista.map(item => item.valor || 0),
        backgroundColor: CORES_GRAFICOS.slice(0, lista.length),
        borderWidth: 1,
      }],
      _total: total,
    };
  };

  const criarDadosBarras = (lista, label) => {
    if (!lista || !lista.length) return null;
    return {
      labels: lista.map(item => item.label || 'N/I'),
      datasets: [{
        label,
        data: lista.map(item => item.valor || 0),
        backgroundColor: 'rgba(54, 162, 235, 0.7)',
        borderColor: 'rgba(54, 162, 235, 1)',
        borderWidth: 1,
      }],
    };
  };

  const criarDadosBarrasAtendentesStella = (lista) => {
    if (!lista || !lista.length) return null;
    return {
      labels: lista.map(item => item.label || 'N/I'),
      datasets: [{
        label: 'Vendas',
        data: lista.map(item => item.valor || 0),
        backgroundColor: lista.map(item =>
          item.is_stella ? 'rgba(153, 102, 255, 0.7)' : 'rgba(54, 162, 235, 0.7)'
        ),
        borderColor: lista.map(item =>
          item.is_stella ? 'rgba(153, 102, 255, 1)' : 'rgba(54, 162, 235, 1)'
        ),
        borderWidth: 1,
      }],
    };
  };

  const criarDadosLinhas = (lista) => {
    if (!lista || !lista.length) return null;

    const empreendimentos = [...new Set(lista.map(item => item.empreendimento))];
    const todasDatas = [...new Set(lista.map(item => item.label))].sort();

    const datasets = empreendimentos.map((emp, idx) => {
      const dadosEmp = lista.filter(item => item.empreendimento === emp);
      const valores = todasDatas.map(data => {
        const ponto = dadosEmp.find(d => d.label === data);
        return ponto ? ponto.valor : 0;
      });

      return {
        label: emp,
        data: valores,
        borderColor: CORES_LINHAS[idx % CORES_LINHAS.length],
        backgroundColor: CORES_GRAFICOS[idx % CORES_GRAFICOS.length],
        tension: 0.3,
        fill: false,
      };
    });

    return {
      labels: todasDatas.map(d => {
        const date = new Date(d);
        return date.toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' });
      }),
      datasets,
    };
  };

  const optionsPizza = (total) => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom', labels: { boxWidth: 12, font: { size: 11 } } },
      tooltip: {
        callbacks: {
          label: (context) => {
            const value = context.parsed || 0;
            const perc = total > 0 ? ((value / total) * 100).toFixed(1) + '%' : '0%';
            return `${context.label}: ${value.toLocaleString('pt-BR')} (${perc})`;
          },
        },
      },
    },
  });

  const optionsBarrasVertical = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (context) => `${context.parsed.y.toLocaleString('pt-BR')}`,
        },
      },
    },
    scales: {
      y: { beginAtZero: true, ticks: { precision: 0 } },
    },
  };

  const optionsLinhas = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom', labels: { boxWidth: 12, font: { size: 11 } } },
      tooltip: {
        callbacks: {
          label: (context) => `${context.dataset.label}: ${context.parsed.y.toLocaleString('pt-BR')}`,
        },
      },
    },
    scales: {
      y: { beginAtZero: true, ticks: { precision: 0 } },
    },
  };

  const dadosVendasPorImobiliaria = useMemo(function() {
    return criarDadosPizza(dados && dados.vendas_por_imobiliaria, 'Vendas por Imobiliária');
  }, [dados]);

  const dadosVendasPorAtendente = useMemo(function() {
    return criarDadosBarrasAtendentesStella(dados && dados.vendas_por_atendente_cav);
  }, [dados]);

  const dadosTopCorretores = useMemo(function() {
    return criarDadosBarras(dados && dados.top_corretores_vendas, 'Vendas');
  }, [dados]);

  const dadosTopEmpreendimentos = useMemo(function() {
    return criarDadosLinhas(dados && dados.top_empreendimentos_vendidos);
  }, [dados]);

  const dadosVendasPorOrigem = useMemo(function() {
    return criarDadosBarras(dados && dados.vendas_por_origem, 'Vendas');
  }, [dados]);

  if (loading) {
    return (
      <div className="loading-overlay">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <div className="tab-conversoes-vendas">
      <div className="d-flex justify-content-end mb-3">
        <Button
          variant="success"
          disabled={isLoadingExcel || !dados}
          onClick={handleExportarExcel}
        >
          {isLoadingExcel ? (
            <Spinner animation="border" size="sm" className="mr-1" />
          ) : (
            <FaFileExcel className="mr-1" />
          )}
          {isLoadingExcel ? 'Exportando...' : 'Exportar Excel'}
        </Button>
      </div>
      <Row className="kpis-container g-3 mb-4">
        <Col xs={12} sm={6} md={4} lg>
          <CardKPI
            titulo="Total de Vendas"
            valor={kpis ? kpis.total_vendas : 0}
            cor="#007bff"
            icone={<FaShoppingCart />}
            loading={loading}
          />
        </Col>
        <Col xs={12} sm={6} md={4} lg>
          <CardKPI
            titulo="Vendas pela CAV"
            valor={kpis ? kpis.vendas_pela_cav : 0}
            cor="#28a745"
            icone={<FaHeadset />}
            loading={loading}
          />
        </Col>
        <Col xs={12} sm={6} md={4} lg>
          <CardKPI
            titulo="Vendas pela Stella"
            valor={kpis ? kpis.vendas_pela_stella : 0}
            cor="#6f42c1"
            icone={<FaRobot />}
            loading={loading}
          />
        </Col>
        <Col xs={12} sm={6} md={4} lg>
          <CardKPI
            titulo="Média Vendas x Leads"
            valor={formatarMedia(kpis ? kpis.media_vendas_x_leads : 0)}
            cor="#17a2b8"
            icone={<FaPercentage />}
            loading={loading}
          />
        </Col>
        <Col xs={12} sm={6} md={4} lg>
          <CardKPI
            titulo="ROI Médio"
            valor={kpis && kpis.roi_disponivel ? ((kpis.roi_medio * 100).toFixed(2) + '%') : 'N/D'}
            cor={kpis && kpis.roi_disponivel ? '#ffc107' : '#6c757d'}
            icone={<FaChartLine />}
            loading={loading}
          />
        </Col>
      </Row>

      <div className="graficos-container">
        <Row className="g-3 mb-3">
          <Col xs={12} lg={6}>
            <div className="grafico-card">
              <h6 className="grafico-titulo">Total de Vendas por Imobiliária</h6>
              <div className="grafico-wrapper" style={{ height: 300 }}>
                {dadosVendasPorImobiliaria ? (
                  <Pie data={dadosVendasPorImobiliaria} options={optionsPizza(dadosVendasPorImobiliaria._total)} />
                ) : (
                  <div className="empty-state">
                    <FaChartBar className="empty-icon" />
                    <span className="empty-text">Sem dados para exibir</span>
                  </div>
                )}
              </div>
            </div>
          </Col>
          <Col xs={12} lg={6}>
            <div className="grafico-card">
              <h6 className="grafico-titulo">Vendas Realizadas pela CAV (por Atendentes + Stella)</h6>
              <div className="grafico-wrapper" style={{ height: 300 }}>
                {dadosVendasPorAtendente ? (
                  <Bar data={dadosVendasPorAtendente} options={optionsBarrasVertical} />
                ) : (
                  <div className="empty-state">
                    <FaChartBar className="empty-icon" />
                    <span className="empty-text">Sem dados para exibir</span>
                  </div>
                )}
              </div>
            </div>
          </Col>
        </Row>

        <Row className="g-3 mb-3">
          <Col xs={12} lg={6}>
            <div className="grafico-card">
              <h6 className="grafico-titulo">Top Corretores em Vendas</h6>
              <div className="grafico-wrapper" style={{ height: 300 }}>
                {dadosTopCorretores ? (
                  <Bar data={dadosTopCorretores} options={optionsBarrasVertical} />
                ) : (
                  <div className="empty-state">
                    <FaChartBar className="empty-icon" />
                    <span className="empty-text">Sem dados para exibir</span>
                  </div>
                )}
              </div>
            </div>
          </Col>
          <Col xs={12} lg={6}>
            <div className="grafico-card">
              <h6 className="grafico-titulo">Top Empreendimentos Vendidos (Top 10)</h6>
              <div className="grafico-wrapper" style={{ height: 300 }}>
                {dadosTopEmpreendimentos ? (
                  <Line data={dadosTopEmpreendimentos} options={optionsLinhas} />
                ) : (
                  <div className="empty-state">
                    <FaChartBar className="empty-icon" />
                    <span className="empty-text">Sem dados para exibir</span>
                  </div>
                )}
              </div>
            </div>
          </Col>
        </Row>

        <Row className="g-3">
          <Col xs={12} lg={6}>
            <div className="grafico-card">
              <h6 className="grafico-titulo">Vendas por Origem</h6>
              <div className="grafico-wrapper" style={{ height: 300 }}>
                {dadosVendasPorOrigem ? (
                  <Bar data={dadosVendasPorOrigem} options={optionsBarrasVertical} />
                ) : (
                  <div className="empty-state">
                    <FaChartBar className="empty-icon" />
                    <span className="empty-text">Sem dados para exibir</span>
                  </div>
                )}
              </div>
            </div>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default TabConversoesVendas;
